import { useDispatch, useSelector } from 'react-redux';
import { LEARNING_PHASES } from '../constants/learningPhases';
import {
  resolveQuizLocked,
  resolveQuizPassed,
} from '../helpers/mapLearningSubmitResponse';
import { clearAnsweres } from '../../redux/survey/actions';
import {
  clearLearningQuizResult,
  goToLearningIntro,
  resetLearningQuestionnaire,
  reviewLearningGuidance as setLearningGuidanceReviewPhase,
  setLearningAttemptsUsed,
  setLearningInviteLocked,
  setLearningPhase,
  setLearningQuizResult,
  startLearningGuidance,
  startLearningQuestionnaire,
} from '../../redux/learning/actions';
import {
  selectEffectiveLearningSurveyId,
  selectLearningAttemptsRemaining,
  selectLearningAttemptsUsed,
  selectLearningAttemptNumber,
  selectLearningCanRetry,
  selectLearningCanStartFlow,
  selectLearningCourse,
  selectLearningMaxAttempts,
  selectLearningNextSubmitAttempt,
  selectLearningPackageError,
  selectLearningPackageLoading,
  selectLearningPassMarkPercent,
  selectLearningPhase,
  selectLearningQuestionnaireSubmitted,
  selectLearningQuizResult,
  selectLearningRecipientEmail,
} from '../../redux/learning/selectors';
import { useLearningPackage } from './useLearningPackage';
import { useLearningQuery } from './useLearningQuery';
import { useTrainingProgress } from './useTrainingProgress';

export function useLearningFlow() {
  const dispatch = useDispatch();
  const { userId, search } = useLearningQuery();
  useLearningPackage();
  useTrainingProgress();

  const course = useSelector(selectLearningCourse);
  const packageLoading = useSelector(selectLearningPackageLoading);
  const packageError = useSelector(selectLearningPackageError);
  const phase = useSelector(selectLearningPhase);
  const attemptsUsed = useSelector(selectLearningAttemptsUsed);
  const attemptNumber = useSelector(selectLearningAttemptNumber);
  const nextSubmitAttempt = useSelector(selectLearningNextSubmitAttempt);
  const maxAttempts = useSelector(selectLearningMaxAttempts);
  const attemptsRemaining = useSelector(selectLearningAttemptsRemaining);
  const passMarkPercent = useSelector(selectLearningPassMarkPercent);
  const quizResult = useSelector(selectLearningQuizResult);
  const recipientEmail = useSelector(selectLearningRecipientEmail);
  const canRetry = useSelector(selectLearningCanRetry);
  const canStartFlow = useSelector(selectLearningCanStartFlow);
  const submitted = useSelector(selectLearningQuestionnaireSubmitted);
  const surveyId = useSelector(selectEffectiveLearningSurveyId);
  const packageId = useSelector((state) => state.learning.packageId);

  const startGuidance = () => {
    if (!canStartFlow) {
      return;
    }
    dispatch(startLearningGuidance());
  };

  const startQuestionnaire = () => {
    if (!canStartFlow) {
      return;
    }

    if (submitted) {
      dispatch(resetLearningQuestionnaire());
      dispatch(clearAnsweres());
    }

    if (quizResult) {
      dispatch(clearLearningQuizResult());
    }

    dispatch(startLearningQuestionnaire());
  };

  const reviewGuidance = () => {
    if (!canRetry) {
      return;
    }

    dispatch(resetLearningQuestionnaire());
    dispatch(clearAnsweres());
    dispatch(setLearningGuidanceReviewPhase());
  };

  const completeQuiz = (result) => {
    const submittedAttemptNumber = attemptsUsed + 1;
    const passed = resolveQuizPassed(result, passMarkPercent);
    const locked = !passed && resolveQuizLocked(result, {
      attemptNumber: submittedAttemptNumber,
      maxAttempts,
    });

    const outcome = {
      scorePercent: result.scorePercent,
      correctCount: result.correctCount,
      totalQuestions: result.totalQuestions,
      resultStatus: result.resultStatus,
      attemptNumber: submittedAttemptNumber,
      completedAt: result.completedAt,
      passed,
      responseId: result.responseId || '',
      email: recipientEmail || '',
    };

    dispatch(setLearningAttemptsUsed(submittedAttemptNumber));
    dispatch(setLearningInviteLocked(locked));
    dispatch(setLearningQuizResult(outcome));

    if (passed) {
      dispatch(setLearningPhase(LEARNING_PHASES.SUCCESS));
      return;
    }

    if (locked) {
      dispatch(setLearningPhase(LEARNING_PHASES.LOCKED));
      return;
    }

    dispatch(setLearningPhase(LEARNING_PHASES.FAIL));
  };

  const retryQuiz = () => {
    if (!canRetry) {
      return;
    }

    dispatch(clearLearningQuizResult());
    dispatch(resetLearningQuestionnaire());
    dispatch(clearAnsweres());
    dispatch(setLearningPhase(LEARNING_PHASES.QUIZ));
  };

  const goToIntro = () => {
    dispatch(goToLearningIntro());
  };

  return {
    packageId,
    userId,
    surveyId,
    search,
    course,
    packageLoading,
    packageError,
    phase,
    attemptsUsed,
    attemptNumber,
    nextSubmitAttempt,
    maxAttempts,
    attemptsRemaining,
    passMarkPercent,
    quizResult,
    canRetry,
    canStartFlow,
    startGuidance,
    startQuestionnaire,
    reviewGuidance,
    completeQuiz,
    retryQuiz,
    goToIntro,
  };
}
